<template>
    <PanelTransition :name="transitionName">
        <div
            v-show="integrationResultIsVisible"
            class="ReaderIntegrationsToolContainer fixed inset-0 z-[58]"
            :class="onlyMobileVisible ? 'flex items-center justify-center bg-black/20' : ''"
            @click.stop="closeIntegrationUsingBackdrop"
        >
            <div
                ref="focusTrapContainer"
                role="dialog"
                aria-modal="true"
                :aria-label="integrationTitle"
                class="w-full bg-theme-bg shadow-lg md:max-h-[80vh]"
                :class="panelClasses"
                :style="!onlyMobileVisible ? panelPositionStyle : {}"
            >
                <div class="p-4">
                    <div class="relative mb-4 flex h-6 items-center justify-center">
                        <template v-if="onlyMobileVisible">
                            <BaseButton
                                icon="arrow-left"
                                :tooltip="localizations[lang].tooltips.back"
                                class="absolute left-0 mr-2 text-theme-icon hover:opacity-80"
                                @click="hideIntegrationResult"
                            />
                        </template>
                        <div class="flex w-full items-center justify-center md:justify-start">
                            <IntegrationsHeader
                                :icon="headerConfig.icon"
                                :icon-custom-fill="headerConfig.iconCustomFill"
                                :title="headerConfig.title"
                            />
                        </div>

                        <template v-if="!onlyMobileVisible">
                            <BaseButton
                                icon="close"
                                :tooltip="localizations[lang].tooltips.close"
                                class="text-theme-toolbar-icon hover:text-theme-text"
                                @click="hideIntegrationResult"
                            />
                        </template>
                    </div>

                    <div class="mt-4">
                        <component
                            :is="currentIntegration"
                            :text="text"
                            :integration-title="integrationTitle"
                            class="IntegrationsHandler-html"
                            @call-close="hideIntegrationResult"
                        />
                    </div>
                </div>
            </div>
        </div>
    </PanelTransition>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';
import IntegrationsHeader from '../ReaderSelectionTool/integrations/HeaderIntegrations.vue';
import PanelTransition from '../Components/PanelTransition.vue';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';
import { calculateLeftPanelPlacement } from '../../js/utils/calculateSelectionToolPosition';

export default {
    components: {
        BaseButton,
        PanelTransition,
        IntegrationsHeader,
    },
    mixins: [focusTrapMixin],
    data() {
        return {
            currentIntegration: null,
            text: '',
            integrationResultIsVisible: false,
            isContentRendered: false,
            focusTrapEnabled: false,
        };
    },
    computed: {
        ...mapState(['integrations', 'localizations', 'translations', 'lang', 'integrationTool', 'device']),
        ...mapGetters(['onlyMobileVisible']),
        headerConfig() {
            const configs = {
                'ai-integrations': {
                    icon: 'sparkles',
                    iconCustomFill: '#FF26E9',
                    title: this.localizations[this.lang].ai,
                },
                dictionary: {
                    icon: 'dictionary_24px',
                    iconCustomFill: null,
                    title: this.localizations[this.lang].dictionary,
                },
                translate: {
                    icon: 'translate-24px',
                    iconCustomFill: null,
                    title: this.localizations[this.lang].translate,
                },
                listen: {
                    icon: 'speaker-24px',
                    iconCustomFill: null,
                    title: this.translations.integrations.listen.title[this.lang],
                },
            };

            return configs[this.currentIntegration] || { icon: '', title: '' };
        },
        integrationTitle() {
            switch (this.currentIntegration) {
                case 'translate':
                    return this.localizations[this.lang]['translate'];
                case 'dictionary':
                    return this.localizations[this.lang]['dictionary'];
                case 'listen':
                    return this.translations.integrations.listen.title[this.lang];
                case 'ai-integrations':
                    return this.localizations[this.lang][this.currentIntegration].buttons[this.integrationTool.action];
                default:
                    return '';
            }
        },
        panelPositionStyle() {
            const viewportHeight = this.device.height || window.innerHeight;
            const panelWidth = 320;

            return {
                ...calculateLeftPanelPlacement({
                    selectionTop: this.$store.state.ui.selectionTool.selectionTop || 0,
                    selectionLeft: this.$store.state.ui.selectionTool.selectionLeft || 0,
                    viewportHeight,
                    panelWidth,
                    panelMaxHeight: viewportHeight * 0.45,
                    allowBottomReposition: this.isContentRendered,
                }),
                width: `${panelWidth}px`,
                maxHeight: '45vh',
                borderRadius: '12px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            };
        },
        panelClasses() {
            if (this.onlyMobileVisible) {
                return 'rounded-lg max-w-md max-h-full overflow-auto h-full rounded-none';
            }
            return 'overflow-y-auto max-w-md';
        },
        transitionName() {
            if (this.onlyMobileVisible) return 'fade';
            return 'slide-left';
        },
    },
    watch: {
        'integrationTool.name': 'showIntegration',
        'integrationTool.text'(text) {
            this.closeSelectionTools();
            this.text = text;
        },
        integrationResultIsVisible: {
            immediate: true,
            handler(isVisible) {
                this.focusTrapEnabled = isVisible;
            },
        },
    },
    created() {
        Object.keys(this.integrations.items).map(integrationSlug => {
            this.$options.components[integrationSlug] = this.integrations.items[integrationSlug].component;
        });
    },
    methods: {
        ...mapMutations(['closeSelectionTools']),
        ...mapActions(['closeIntegrationTool']),
        onFocusTrapEscape() {
            this.hideIntegrationResult();
        },
        handleIntegration(integrationSlug, text) {
            this.unsetCurrentIntegration();
            this.currentIntegration = integrationSlug;
            this.text = text;
            this.integrationResultIsVisible = true;
            this.isContentRendered = false;
            this.integrations.items[this.currentIntegration].isActive = true;

            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.isContentRendered = true;
                });
            });
        },
        unsetCurrentIntegration() {
            if (this.currentIntegration !== null) {
                this.integrations.items[this.currentIntegration].isActive = false;
            }
            this.currentIntegration = null;
        },
        hideIntegrationResult() {
            this.unsetCurrentIntegration();
            this.integrationResultIsVisible = false;
            this.isContentRendered = false;
            this.closeIntegrationTool();
        },
        showIntegration() {
            if (!this.integrationTool.opened) return;
            this.text = this.integrationTool.text;
            this.handleIntegration(this.integrationTool.name, this.integrationTool.text);
        },
        closeIntegrationUsingBackdrop(event) {
            if (this.$el !== event.target) return;
            this.hideIntegrationResult();
        },
    },
};
</script>
